import type { Account } from '@interfaces/account';
import { getMaskedAccountName } from '@interfaces/account';

import { CLOSED_ACCOUNT_STATES } from '@ovComponents/4-module/workflowCompletion/subSteps/createAccountGroup/closedAccountStates';
import { colors } from '@ovComponents/legacyColor';
import { generateClientNameString } from '@utils/generateClientNameString';
import { formatMoneyValue } from '@utils/moneyFormatting';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from 'hephaestus/web';
import {
  DragIndicator as DragIndicatorIcon,
  RemoveCircleOutlined as RemoveCircleOutlineIcon,
} from 'hephaestus/webIcons';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

export type DraggableAccountsProps = {
  droppableId: string;
  accounts: Account[];
  variant: 'create' | 'edit';
  hasMore?: boolean;
  loading?: boolean;
  handleLoadMore?: () => void;
  onRemoveAccount?: (accountId: string, account: Account) => void;
  showRemoveIcon?: boolean;
  isGroupSection?: boolean;
  objectType?: 'USER' | 'HOUSEHOLD' | 'ORGANIZATION';
  isSearching?: boolean;
  showEmptyText?: boolean;
};

const hasAccounts = (accounts: Account[]) => accounts.length > 0;

const getListStyle = (
  variant: 'create' | 'edit',
  isGroupSection: boolean,
  _isSearching: boolean,
) => {
  return {
    width:
      variant === 'edit'
        ? isGroupSection
          ? 'calc(100% + 48px)'
          : 'calc(100% + 10px)'
        : isGroupSection
          ? 'calc(100% + 72px)'
          : 'calc(100% + 16px)',
    padding:
      variant === 'edit'
        ? isGroupSection
          ? '12px 0px'
          : '16px 18px'
        : isGroupSection
          ? '16px 0px'
          : '16px 16px',
    marginLeft:
      variant === 'edit' ? (isGroupSection ? '-24px' : '-28px') : '-32px',
  };
};

export const DraggableAccounts = ({
  droppableId,
  accounts = [],
  variant = 'create',
  hasMore = false,
  loading = false,
  handleLoadMore,
  onRemoveAccount,
  showRemoveIcon = false,
  isGroupSection = false,
  objectType,
  isSearching = false,
  showEmptyText = true,
}: DraggableAccountsProps) => {
  const { t } = useTranslation('client');
  const theme = useTheme();

  return (
    <>
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            style={{
              border:
                !hasAccounts(accounts) &&
                isGroupSection &&
                !isSearching &&
                showEmptyText
                  ? `1px dashed ${variant === 'create' ? colors.neutral600 : colors.error}`
                  : undefined,
              borderRadius: '4px',
              marginTop: isGroupSection ? '16px' : undefined,
              marginBottom: variant === 'create' ? '8px' : undefined,
              display: 'flex',
              flexDirection: 'column',
              alignItems: !hasAccounts(accounts) ? 'center' : 'flex-start',
              justifyContent: !hasAccounts(accounts) ? 'center' : 'flex-start',
              background:
                snapshot.isDraggingOver &&
                !hasAccounts(accounts) &&
                isGroupSection
                  ? '#F6F7F8'
                  : undefined,
              minHeight: '60px',
            }}
          >
            {!hasAccounts(accounts) &&
              showEmptyText &&
              isGroupSection &&
              !snapshot.isDraggingOver && (
                <Typography
                  variant='bodyLarge'
                  color={
                    variant === 'create'
                      ? theme.color.onSurfaceVariant
                      : theme.color.negative
                  }
                >
                  {variant === 'create'
                    ? t('accountsGroup.dragAccounts')
                    : !isSearching && t('editAccountGroup.noAccountsInGroup')}
                </Typography>
              )}
            {hasAccounts(accounts) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  ...getListStyle(variant, isGroupSection, isSearching),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    marginLeft: isGroupSection ? '16px' : undefined,
                  }}
                />

                {objectType !== 'USER' && (
                  <Typography
                    variant='labelSmall'
                    sx={{
                      width: '240px',
                      color: theme.color.onSurfaceVariant,
                    }}
                  >
                    {t('accountsGroup.columnHeaders.client')}
                  </Typography>
                )}
                <Typography
                  variant='labelSmall'
                  sx={{
                    width: objectType !== 'USER' ? '150px' : '270px',
                    color: theme.color.onSurfaceVariant,
                    paddingRight: '6px',
                  }}
                >
                  {t('accountsGroup.columnHeaders.accountName')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{
                      width: objectType !== 'USER' ? '150px' : '270px',
                      textAlign: 'right',
                      color: theme.color.onSurfaceVariant,
                    }}
                  >
                    {t('accountsGroup.columnHeaders.marketValue')}
                  </Typography>
                  {showRemoveIcon && (
                    <Box
                      sx={{
                        width:
                          isGroupSection && variant === 'create'
                            ? '40px'
                            : '26px',
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}
            {accounts.map((acc, index) => (
              <Draggable
                key={acc.id || `account-${index}`}
                draggableId={acc.id || `account-${index}`}
                index={index}
              >
                {(draggableProvided, draggableSnapshot) => (
                  <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    {...draggableProvided.dragHandleProps}
                    className='draggable-row'
                    style={{
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      position: 'relative',
                      background: draggableSnapshot.isDragging
                        ? colors?.neutral30
                        : 'none',
                      ...getListStyle(variant, isGroupSection, isSearching),
                      ...draggableProvided.draggableProps.style,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingX: isGroupSection ? '8px' : undefined,
                        paddingRight: '-8px',
                        justifyContent: 'center',
                        marginLeft: isGroupSection ? undefined : '-8px',
                        transition: 'opacity 0.2s',
                        width: variant === 'edit' ? 8 : 16,
                        opacity: draggableSnapshot.isDragging ? 1 : 0,
                        marginRight: '8px',
                        '.draggable-row:hover &': { opacity: 1 },
                      }}
                    >
                      <DragIndicatorIcon
                        sx={{ color: theme.color.onSurface, width: '16px' }}
                      />
                    </Box>
                    {objectType !== 'USER' && (
                      <Typography
                        variant='bodyMedium'
                        sx={{
                          width: '240px',
                          paddingRight: '20px',
                          wordBreak: 'break-all',
                          fontWeight: 500,
                        }}
                        color={
                          CLOSED_ACCOUNT_STATES.includes(acc.state)
                            ? 'textSecondary'
                            : undefined
                        }
                      >
                        {generateClientNameString(acc.user, false, true)}
                      </Typography>
                    )}
                    <Typography
                      variant='bodyMedium'
                      sx={{
                        width: objectType !== 'USER' ? '150px' : '270px',
                        wordBreak: 'break-all',
                        paddingRight: '20px',
                        fontWeight: 500,
                      }}
                      color={
                        CLOSED_ACCOUNT_STATES.includes(acc.state)
                          ? 'textSecondary'
                          : undefined
                      }
                    >
                      {getMaskedAccountName(acc, true)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant='bodyMedium'
                        sx={{
                          width: objectType !== 'USER' ? '150px' : '270px',
                          wordBreak: 'break-all',
                          textAlign: 'right',
                          fontWeight: 500,
                        }}
                        color={
                          CLOSED_ACCOUNT_STATES.includes(acc.state)
                            ? 'textSecondary'
                            : undefined
                        }
                      >
                        {formatMoneyValue(
                          acc.statistics?.marketValueCents,
                          '$',
                          2,
                          false,
                        )}
                      </Typography>
                      {showRemoveIcon && onRemoveAccount && (
                        <Box
                          sx={{ px: variant === 'create' ? '8px' : undefined }}
                        >
                          <Tooltip
                            title={t('accountsGroup.removeFromGroup')}
                            placement='top'
                          >
                            <IconButton
                              aria-label='delete'
                              size='xsmall'
                              onClick={() => onRemoveAccount(acc.id, acc)}
                              sx={{
                                color: theme.color.onSurface,
                                transition: 'opacity 0.2s',
                                opacity: draggableSnapshot.isDragging ? 1 : 0,
                                '.draggable-row:hover &': { opacity: 1 },
                              }}
                            >
                              <RemoveCircleOutlineIcon
                                fontSize='inherit'
                                sx={{ width: '16px' }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {hasMore &&
              (!isGroupSection || (isGroupSection && variant === 'edit')) && (
                <Button
                  variant='text'
                  size='small'
                  fullWidth
                  onClick={handleLoadMore}
                  loading={loading}
                  sx={{
                    color:
                      theme.color.primary &&
                      `${theme.color.primary} !important`,
                  }}
                >
                  {t('accountsGroup.seeMore')}
                </Button>
              )}
          </div>
        )}
      </Droppable>
      {isGroupSection && hasAccounts(accounts) && <Divider />}
    </>
  );
};
